import axios from 'axios'
import axiosInstance from './axiosInstance'
import { AXIOS_METHODS } from '../utils/constants/enum'
import { EndeavorAuthType } from '../utils/types/auth'
import { maskImagesDomain } from '../utils/helper-functions'
import { HeartBeatData } from '../utils/types/Endeavor'

interface AxiosResponseType<DataType> {
    success: boolean
    data: DataType
    message: string
}

export class AxiosClass {
    endeavorToken: EndeavorAuthType | undefined = undefined

    async csApiCall<PayloadType, DataType>(
        query: PayloadType
    ): Promise<AxiosResponseType<DataType> | undefined> {
        try {
            const data = JSON.stringify({
                query: `query MyQuery ${query}`,
                variables: {},
            })
            const headers = {
                access_token: `${process.env.REACT_APP_CS_DELIVERY_TOKEN}`,
                'Content-Type': 'application/json',
            }
            const response = await axios({
                method: AXIOS_METHODS.POST,
                url: `${process.env.REACT_APP_CS_API_URL}`,
                headers,
                data,
            })
            // To mask contentstack images url with custom domain
            if (response?.data) {
                maskImagesDomain(response.data)
                return response.data
            }
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async csCdnApiCall<DataType>(
        method: AXIOS_METHODS,
        url: string
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                api_key: `${process.env.REACT_APP_CS_API_KEY}`,
                access_token: `${process.env.REACT_APP_CS_DELIVERY_TOKEN}`,
            }
            const response = await axios({
                method,
                headers,
                url: `${process.env.REACT_APP_CS_CDN_URL}${url}`,
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    setEndeavorAuthToken(token: EndeavorAuthType) {
        this.endeavorToken = token
    }

    //This method is for MWH middleware API
    async mwApiCall<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url: string,
        params?: PayloadType | undefined,
        dataSet?: PayloadType | undefined
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                Authorization: `Bearer ${this.endeavorToken?.authorisationToken}`,
            }
            const response = await axios({
                method: method,
                url: `${process.env.REACT_APP_MWH_API_URL}${url}${
                    params ?? ''
                }`,
                headers: headers,
                data: dataSet,
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    //This method is for Endeavor platform API
    async edvApiCall<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url: string,
        params?: PayloadType | undefined,
        dataSet?: PayloadType | Array<HeartBeatData> | undefined,
        isStagUrlForDoris = false
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                Authorization: `Bearer ${this.endeavorToken?.authorisationToken}`,
                realm: process.env.REACT_APP_MWH_ENDEAVOR_REALM_KEY
                    ? process.env.REACT_APP_MWH_ENDEAVOR_REALM_KEY
                    : '',
                'x-api-key': process.env.REACT_APP_MWH_ENDEAVOR_X_API_KEY
                    ? process.env.REACT_APP_MWH_ENDEAVOR_X_API_KEY
                    : '',
            }
            const response = await axiosInstance({
                method: method,
                url: isStagUrlForDoris
                    ? url
                    : `${process.env.REACT_APP_MWH_ENDEAVOR_API_URL}/${url}${
                          params ?? ''
                      }`,
                headers: headers,
                data: dataSet,
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async splitApiCall<PayloadType, DataType>(
        url: string,
        dataSet?: PayloadType | undefined
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                'x-api-token': process.env.REACT_APP_SPLIT_X_API_TOKEN
                    ? process.env.REACT_APP_SPLIT_X_API_TOKEN
                    : '',
                'content-type': 'application/json',
                'x-app': 'WebApp',
            }
            const response = await axios({
                method: AXIOS_METHODS.POST,
                url: `${process.env.REACT_APP_SPLIT_URL}${url}`,
                headers: headers,
                data: JSON.stringify(dataSet),
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async iterableApiCall<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url: string,
        params?: PayloadType | undefined,
        dataSet?: PayloadType | undefined
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                'Api-key': process.env.REACT_APP_ITERABLE_API_KEY
                    ? process.env.REACT_APP_ITERABLE_API_KEY
                    : '',
            }
            const response = await axios({
                method: method,
                url: `${process.env.REACT_APP_ITERABLE_BASE_PATH}${url}`,
                headers: headers,
                data: dataSet,
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    async zendeskApiCall<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url: string,
        dataSet?: PayloadType
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                'content-type': 'application/json',
            }
            const response = await axios({
                method: method,
                url: `${process.env.REACT_APP_ZENDESK_URL}${url}`,
                headers: headers,
                data: dataSet,
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }

    //This method is for MWH middleware API V2
    async mwApiV2Call<PayloadType, DataType>(
        method: AXIOS_METHODS,
        url: string,
        params?: PayloadType | undefined,
        dataSet?: PayloadType | undefined
    ): Promise<DataType | undefined> {
        try {
            const headers = {
                Authorization: `Bearer ${this.endeavorToken?.authorisationToken}`,
            }
            const response = await axios({
                method: method,
                url: `${process.env.REACT_APP_MWH_API_V2_URL}${url}${
                    params ?? ''
                }`,
                headers: headers,
                data: dataSet,
            })
            if (response?.data) return response.data
            return undefined
        } catch (error) {
            console.error(error) // as per sonar cloud
            throw error
        }
    }
}

export const Axios = new AxiosClass()
